/**
 * Browser host: glues a FlashPlayer to a <canvas> — viewport (stage scale mode, letterboxing,
 * devicePixelRatio), per-frame rendering with CanvasRenderer, input (BrowserInput), cursor,
 * audio unlock on the first gesture and SharedObject flushing when the page is hidden/closed, pause/resume of the
 * frame loop and the audio (the app in the background), and on touch screens a view that keeps a focused text field
 * visible above the on-screen keyboard.
 *
 * Typical use (see src/flash/render/README.md):
 *   const platform = new BrowserPlatform();
 *   const player = new FlashPlayer(platform);
 *   const library = await AssetLibrary.load(platform, 'assets');
 *   await platform.loadFonts(library.fonts);
 *   const host = new BrowserHost({ player, canvas, platform, library });
 *   player.setRoot(gameRoot);
 *   host.start();
 */
import type { FlashPlayer } from '../player/FlashPlayer';
import { SharedObject } from '../net/SharedObject';
import { rt } from '../player/runtime';
import { CanvasRenderer, CanvasRendererOptions, Viewport } from '../render/CanvasRenderer';
import { BrowserInput } from './browser-input';
import type { FontResolver } from './browser-fonts';

export type HostScaleMode = 'showAll' | 'exactFit' | 'noBorder' | 'noScale';

/**
 * PORT (wide screens): the largest part of the stage a wide host shows, centred on the stage (stage px). A screen of
 * another shape than the stage shows the stage whole (showAll) plus the margin around it up to these limits, like
 * Flash's showAll shows what lies beyond the stage's edges; only beyond them are there letterbox bars.
 */
export interface WideLimits {
  width: number;
  height: number;
  /**
   * A screen a little narrower than the stage shows it as tall as the screen when at least this much of its width
   * stays in view (the sides cut off evenly), rather than thin bars above and below (default: the stage's width).
   */
  minWidth?: number;
}

/** The part of the stage a wide host may show at most (stage coordinates): `limits` centred on the stage. */
export function wideContent(stageW: number, stageH: number, limits: WideLimits): StageRect {
  const w = Math.max(stageW, limits.width);
  const h = Math.max(stageH, limits.height);
  return { x: (stageW - w) / 2, y: (stageH - h) / 2, width: w, height: h };
}

/**
 * The part of `content` (stage coordinates) a canvas of `W` x `H` device px shows through viewport `vp`, or null
 * when none of it.
 */
export function shownArea(vp: Viewport, W: number, H: number, content: StageRect): StageRect | null {
  const sy = vp.scaleY ?? vp.scale;
  const x0 = Math.max(content.x, -vp.tx / vp.scale);
  const y0 = Math.max(content.y, -vp.ty / sy);
  const x1 = Math.min(content.x + content.width, (W - vp.tx) / vp.scale);
  const y1 = Math.min(content.y + content.height, (H - vp.ty) / sy);
  // (+ 0: never -0)
  return x1 > x0 && y1 > y0 ? { x: x0 + 0, y: y0 + 0, width: x1 - x0, height: y1 - y0 } : null;
}

/**
 * Viewport for a canvas of `cssW` x `cssH` CSS pixels at device pixel ratio `dpr`
 * (Flash StageScaleMode semantics, centred like StageAlign ""). Returns the backing-store size
 * and the stage -> device mapping; translation is snapped to whole device pixels.
 * PORT: `wide` (showAll only) also returns `visible`, the part of the stage the canvas shows: the stage and the margin
 * around it the screen's shape leaves, up to the wide limits (without `wide`: the stage rectangle).
 */
export function computeViewport(
  cssW: number,
  cssH: number,
  dpr: number,
  stageW: number,
  stageH: number,
  mode: HostScaleMode = 'showAll',
  wide: WideLimits | null = null,
): { width: number; height: number; viewport: Viewport; visible: StageRect } {
  const W = Math.max(1, Math.round(cssW * dpr));
  const H = Math.max(1, Math.round(cssH * dpr));
  let sx: number;
  let sy: number;
  switch (mode) {
    case 'exactFit':
      sx = W / stageW;
      sy = H / stageH;
      break;
    case 'noBorder':
      sx = sy = Math.max(W / stageW, H / stageH);
      break;
    case 'noScale':
      sx = sy = dpr;
      break;
    default:
      sx = sy = Math.min(W / stageW, H / stageH);
      // PORT (wide screens): a screen a little narrower than the stage (a 4:3 tablet) fills its height.
      // (The screen's shape in CSS px: the backing store's rounding must not flip the choice.)
      if (wide && W / stageW < H / stageH && (cssW * stageH) / cssH >= Math.min(stageW, wide.minWidth ?? stageW) - 1e-9) sx = sy = H / stageH;
  }
  const tx = Math.round((W - stageW * sx) / 2);
  const ty = Math.round((H - stageH * sy) / 2);
  const viewport = { scale: sx, scaleY: sy, tx, ty };
  const stage = { x: 0, y: 0, width: stageW, height: stageH };
  const visible = wide && mode === 'showAll' ? (shownArea(viewport, W, H, wideContent(stageW, stageH, wide)) ?? stage) : stage;
  return { width: W, height: H, viewport, visible };
}

/** A rectangle in stage coordinates. */
export interface StageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * PORT: the viewport of a small screen zoomed into a part of the stage (e.g. a form: FocusRect), or null when the
 * whole stage is shown large enough. The zoom applies when showAll would draw the stage smaller than its own size
 * (less than one CSS pixel per stage pixel: a phone) and fitting `rect` makes it larger. The rectangle is centred in
 * the canvas; along an axis where the whole stage fits it is centred instead, and the stage never leaves a gap at an
 * edge it could fill. Input maps through the same viewport (clientToStage), so the zoomed stage is fully usable.
 * `content` is what may be shown (default the stage; a wide host: the stage and its margin, wideContent). `cssH` is the
 * height the rectangle must fit in, from the canvas' top: above an on-screen keyboard it is less than the canvas.
 */
export function computeFocusViewport(
  cssW: number,
  cssH: number,
  dpr: number,
  stageW: number,
  stageH: number,
  rect: StageRect,
  content: StageRect = { x: 0, y: 0, width: stageW, height: stageH },
): Viewport | null {
  if (!(rect.width > 0 && rect.height > 0 && stageW > 0 && stageH > 0)) return null;
  const cssScale = Math.min(cssW / stageW, cssH / stageH);
  if (!(cssScale < 1)) return null;
  const W = Math.max(1, Math.round(cssW * dpr));
  const H = Math.max(1, Math.round(cssH * dpr));
  const s = Math.min(W / rect.width, H / rect.height);
  if (!(s > cssScale * dpr * 1.05)) return null;
  const place = (size: number, c0: number, clen: number, start: number, length: number): number => {
    if (clen * s <= size) return Math.round((size - clen * s) / 2 - c0 * s);
    const t = size / 2 - (start + length / 2) * s;
    return Math.round(Math.min(-c0 * s, Math.max(size - (c0 + clen) * s, t)));
  };
  return { scale: s, scaleY: s, tx: place(W, content.x, content.width, rect.x, rect.width), ty: place(H, content.y, content.height, rect.y, rect.height) };
}

/**
 * Device pixel ratio actually used for the backing store: the (capped) devicePixelRatio, further limited so
 * that one stage pixel never maps to more than `maxStageScale` device pixels (floor 0.5). Rendering cost grows
 * with device pixels while the extracted art has a fixed resolution (shape atlases are rasterized at 2x).
 */
export function effectivePixelRatio(
  dpr: number,
  cssW: number,
  cssH: number,
  stageW: number,
  stageH: number,
  mode: HostScaleMode = 'showAll',
  maxStageScale = 2,
): number {
  if (!Number.isFinite(maxStageScale) || maxStageScale <= 0) return dpr;
  const sx = cssW / (stageW || 1);
  const sy = cssH / (stageH || 1);
  const cssScale = mode === 'noScale' ? 1 : mode === 'showAll' ? Math.min(sx, sy) : Math.max(sx, sy);
  if (!(cssScale > 0)) return dpr;
  return Math.max(0.5, Math.min(dpr, maxStageScale / cssScale));
}

/** Client (CSS) coordinates -> stage coordinates for a canvas with the given layout. */
export function clientToStage(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  canvasW: number,
  canvasH: number,
  vp: Viewport,
): [number, number] {
  const dx = rect.width > 0 ? ((clientX - rect.left) * canvasW) / rect.width : 0;
  const dy = rect.height > 0 ? ((clientY - rect.top) * canvasH) / rect.height : 0;
  return [(dx - vp.tx) / vp.scale, (dy - vp.ty) / (vp.scaleY ?? vp.scale)];
}

export interface BrowserHostOptions {
  player: FlashPlayer;
  canvas: HTMLCanvasElement;
  /** Platform (for fonts / audio unlock); any object with optional `fonts` and `audio.resume()`. */
  platform?: { fonts?: FontResolver; audio?: any } | null;
  /** Asset library (lazy decoding of shape images while rendering). */
  library?: { images: { load(url: string): Promise<unknown> } } | null;
  /** Default: the stage's scaleMode ("showAll" unless the game changes it). */
  scaleMode?: HostScaleMode;
  /** Fixed device pixel ratio (default: window.devicePixelRatio capped by maxPixelRatio). */
  pixelRatio?: number;
  maxPixelRatio?: number;
  /**
   * Upper bound of device pixels per stage pixel (default Infinity = native device resolution). A finite value
   * caps the backing store (the canvas is then scaled by CSS, which blurs the whole frame including text).
   */
  maxStageScale?: number;
  /**
   * Lower bound of backing-store pixels per CSS pixel for dynamic resolution (default 1): below the CSS size the
   * browser upscales the canvas, blurring everything. HiDPI screens may drop from native DPR down to this.
   */
  minPixelRatio?: number;
  /**
   * Dynamic resolution (default true): when the measured render time stays high (software rasterization,
   * weak GPU) the backing store is lowered from native DPR towards the CSS resolution (minPixelRatio), and
   * raised again when rendering is cheap. Simulation is unaffected.
   */
  adaptiveResolution?: boolean;
  letterboxColor?: string;
  /** Clip content outside the stage rectangle (default true). */
  letterbox?: boolean;
  renderer?: CanvasRendererOptions;
  /** Attach DOM input (default true). */
  input?: boolean;
  /** Keyboard event source (default: window). */
  keyTarget?: EventTarget | null;
  /**
   * PORT: the part of the stage a small screen zooms into (computeFocusViewport), asked every frame; null = the whole
   * stage. The game's sign-in form uses it: on a phone the full 800x600 stage would make its text a few pixels tall.
   */
  focusRect?: (() => StageRect | null) | null;
  /** Flush SharedObjects on pagehide / visibility hidden (default true). */
  flushOnHide?: boolean;
  /**
   * PORT (mobile): dynamic resolution reacts to very slow frames (median over QUICK_ADAPT_MS) after QUICK_ADAPT_SAMPLES
   * renders and steps straight to the scale predicted to fit, instead of 0.25 steps every 90 renders (default false:
   * desktop behaviour unchanged). For touch screens and the app, where a slow device is common.
   */
  quickAdapt?: boolean;
  /** Touch hover after a finger is lifted (ms; BrowserInputOptions.touchHoverMs, default 0). */
  touchHoverMs?: number;
  /** PORT (touch): target slop for fingers (CSS px; BrowserInputOptions.touchSlopPx, default 0: off). */
  touchSlopPx?: number;
  /** PORT (touch): long press that shows the hover without a click (ms; BrowserInputOptions.longPressMs, default 0: off). */
  longPressMs?: number;
  /** PORT (touch): where the touch aids apply (BrowserInputOptions.touchAssistAt). */
  touchAssistAt?: ((target: any, stageX: number, stageY: number) => boolean) | null;
  /** PORT (touch): a finger's tap that waits for the player's confirmation (BrowserInputOptions.holdTap). */
  holdTap?: ((target: any, stageX: number, stageY: number) => boolean) | null;
  /**
   * PORT (mobile): dynamic resolution never goes below this many device px per stage px when the device has them
   * (default: the CSS-size floor only); a function is asked every frame (e.g. 1 on the menus, 0 in a battle). 1 on a
   * phone's menus: the game's text keeps at least the pixels it had in the original at 800x600 (a phone in landscape
   * shows the stage at ~0.69 CSS px per stage px: the CSS-size floor would draw 12 px text with 8 px and let the
   * compositor blur it up to the screen's DPR). A floor that rises (a battle ends) raises the backing store at once.
   */
  minStageScale?: number | (() => number);
  /**
   * Touch screen (default: a coarse primary pointer; `setTouchScreen(true)` after the first real touch of a touch
   * laptop): while an on-screen keyboard covers the bottom of the canvas (setKeyboardInset) or the canvas got markedly
   * shorter (a browser that resizes the page for it) and a text field has the focus, the view zooms to that field in
   * the part of the canvas above the keyboard (keyboardRect); the game keeps its size.
   */
  touchScreen?: boolean;
  /**
   * PORT (wide screens): show the margin around the stage the screen's shape leaves, up to these limits (showAll
   * only), instead of letterbox bars: Stage.visibleRect tells the game what is shown. A function is asked on every
   * resize check (e.g. only in touch mode); null / absent: the stage alone, letterboxed.
   */
  wide?: WideLimits | null | (() => WideLimits | null);
  /**
   * PORT: whether a frame that would show objects whose shape images are not decoded yet (lazily loaded art: a new
   * screen's panels, its wide margin) keeps the last frame on the canvas instead, for up to HOLD_FRAME_MS while they
   * load (a screen appears whole, not its texts and buttons first over a black margin); asked before each render
   * (e.g. true on the menus, false in a battle). Absent: every frame is shown as it is.
   */
  holdFrame?: (() => boolean) | null;
}

/** The longest a host keeps its last frame while a new screen's art loads (BrowserHostOptions.holdFrame; ms). */
export const HOLD_FRAME_MS = 2500;

/**
 * PORT: the part of the stage kept visible above an on-screen keyboard: the focused field with a margin (its label
 * above, a line below), or null without a focused field or without a keyboard.
 */
export function keyboardRect(field: StageRect | null, keyboard: boolean): StageRect | null {
  if (!field || !keyboard) return null;
  return { x: field.x - 16, y: field.y - 28, width: field.width + 32, height: field.height + 56 };
}

/** quickAdapt: renders sampled before acting on very slow frames, and what counts as very slow (median, ms). */
export const QUICK_ADAPT_SAMPLES = 20;
export const QUICK_ADAPT_MS = 28;

/**
 * A canvas shorter than this share of its tallest height at the same width has an on-screen keyboard under it (a
 * browser that shrinks the page for the keyboard instead of reporting it: setKeyboardInset).
 */
export const KEYBOARD_HEIGHT_RATIO = 0.85;

/** Insets of the canvas' edges (CSS px): notches, rounded corners, system bars. */
export interface CssInsets {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * PORT: `vp` moved up just enough that `rect` (stage coordinates) ends above device row `availH` (the top of an
 * on-screen keyboard), never so far that its top leaves the canvas; unchanged when it is already above.
 */
export function liftAbove(vp: Viewport, rect: StageRect, availH: number): Viewport {
  const sy = vp.scaleY ?? vp.scale;
  const bottom = vp.ty + (rect.y + rect.height) * sy;
  if (bottom <= availH) return vp;
  const top = vp.ty + rect.y * sy;
  const up = Math.min(bottom - availH, Math.max(0, top));
  return { ...vp, ty: Math.round(vp.ty - up) };
}

export class BrowserHost {
  readonly player: FlashPlayer;
  readonly canvas: HTMLCanvasElement;
  readonly renderer: CanvasRenderer;
  readonly input: BrowserInput | null;
  viewport: Viewport = { scale: 1, tx: 0, ty: 0 };
  private readonly opts: BrowserHostOptions;
  private dirty = true;
  private lastCss = '';
  private readonly cleanup: Array<() => void> = [];
  private prevOnFrame: FlashPlayer['onFrame'] = null;
  private started = false;

  constructor(opts: BrowserHostOptions) {
    this.opts = opts;
    this.player = opts.player;
    this.canvas = opts.canvas;
    const ctx = this.canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('Canvas 2D is not available');
    const lib = opts.library ?? null;
    this.renderer = new CanvasRenderer(ctx, {
      fonts: opts.platform?.fonts,
      imageLoader: lib ? (url) => lib.images.load(url) : null,
      onImageLoaded: () => this.requestRender(),
      // PORT (mobile performance): a frame redraws only what changed (src/flash/render/damage.ts).
      damage: true,
      ...opts.renderer,
    });
    this.input =
      opts.input === false
        ? null
        : new BrowserInput(this.player as any, this.canvas, {
            toStage: (x, y) => this.toStage(x, y),
            keyTarget: opts.keyTarget,
            onUserGesture: () => this.unlockAudio(),
            // (what the input changes on the display list is tracked: the next frame may be a partial one)
            onInput: () => {
              this.dirty = true;
            },
            touchHoverMs: opts.touchHoverMs,
            touchSlopPx: opts.touchSlopPx,
            longPressMs: opts.longPressMs,
            touchAssistAt: opts.touchAssistAt,
            holdTap: opts.holdTap,
          });
    const g: any = globalThis as any;
    this.touchScreen = opts.touchScreen ?? !!g.matchMedia?.('(pointer: coarse)')?.matches;
  }

  private touchScreen: boolean;

  /** The page is used by touch from now on (the first real touch of a touch laptop): the keyboard view applies. */
  setTouchScreen(on: boolean): void {
    this.touchScreen = on;
    this.lastCss = '';
  }
  private tallest = { width: -1, height: 0 };
  private paused = false;

  /** The frame loop and the audio are stopped (the app is in the background). */
  get isPaused(): boolean {
    return this.paused;
  }

  /**
   * The app went to the background: the frame loop stops (no simulation, no rendering), held keys are released, the
   * audio output is suspended and SharedObjects are flushed. `resume()` continues where it stopped (no catch-up of
   * the time spent away: the loop starts afresh).
   */
  pause(): void {
    if (this.paused || !this.started) return;
    this.paused = true;
    this.player.stopLoop();
    this.input?.releaseAllKeys();
    const a = this.opts.platform?.audio;
    if (a && typeof a.suspend === 'function') void Promise.resolve(a.suspend()).catch(() => undefined);
    SharedObject.flushAll();
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    if (!this.started) return;
    this.lastCss = '';
    this.requestRender();
    this.unlockAudio();
    this.player.start();
  }

  /** The keyboard view (keyboardRect) for the focused text field (with the button of its row it names: $keyboardWith), if any. */
  private keyboardFocus(cssW: number, cssH: number): StageRect | null {
    if (!this.touchScreen) return null;
    if (cssW !== this.tallest.width) this.tallest = { width: cssW, height: cssH };
    else if (cssH > this.tallest.height) this.tallest.height = cssH;
    const f: any = (this.player.stage as any).focus;
    if (!f || f.type !== 'input' || !f.stage) return null;
    let b: any;
    try {
      b = f.getBounds(f.stage);
      // The button of the field's row that its Enter stands for, shown with it (TextField.$keyboardWith).
      const w = f.$keyboardWith && f.parent && typeof f.parent.getChildByName === 'function' ? f.parent.getChildByName(f.$keyboardWith) : null;
      if (w && w.visible && w.stage) {
        const o = w.getBounds(f.stage);
        const x = Math.min(b.x, o.x);
        const y = Math.min(b.y, o.y);
        b = { x, y, width: Math.max(b.x + b.width, o.x + o.width) - x, height: Math.max(b.y + b.height, o.y + o.height) - y };
      }
    } catch {
      return null;
    }
    const shrunk = this.tallest.height > 0 && cssH < this.tallest.height * KEYBOARD_HEIGHT_RATIO;
    return keyboardRect({ x: b.x, y: b.y, width: b.width, height: b.height }, this.keyboardInset > 0 || shrunk);
  }

  private keyboardInset = 0;
  private safeInsets: CssInsets = { left: 0, top: 0, right: 0, bottom: 0 };

  /**
   * PORT: an on-screen keyboard covers the bottom `cssPx` of the canvas (0: none). The canvas keeps its size; while a
   * text field has the focus the view shows it in the part above the keyboard.
   */
  setKeyboardInset(cssPx: number): void {
    const v = Number.isFinite(cssPx) && cssPx > 0 ? Math.round(cssPx) : 0;
    if (v === this.keyboardInset) return;
    this.keyboardInset = v;
    this.lastCss = '';
  }

  /** The on-screen keyboard's height over the canvas (CSS px; 0: none). */
  get keyboardHeight(): number {
    return this.keyboardInset;
  }

  /** PORT: the canvas' edges covered by notches, rounded corners or system bars (CSS px): Stage.safeRect leaves them out. */
  setSafeInsets(insets: CssInsets): void {
    const r = (v: number) => (Number.isFinite(v) && v > 0 ? Math.round(v) : 0);
    const n = { left: r(insets.left), top: r(insets.top), right: r(insets.right), bottom: r(insets.bottom) };
    const o = this.safeInsets;
    if (n.left === o.left && n.top === o.top && n.right === o.right && n.bottom === o.bottom) return;
    this.safeInsets = n;
    this.lastCss = '';
  }

  /** The wide limits now (null: the stage alone). */
  private wideLimits(): WideLimits | null {
    const w = this.opts.wide;
    try {
      const v = typeof w === 'function' ? w() : (w ?? null);
      return v && v.width > 0 && v.height > 0 ? v : null;
    } catch {
      return null;
    }
  }

  /** PORT (wide screens): the part of the stage the canvas shows while the host is wide (null: the stage alone). */
  visibleArea: StageRect | null = null;

  private unlockAudio(): void {
    const a = this.opts.platform?.audio;
    if (a && typeof a.resume === 'function') void a.resume();
  }

  private pixelRatio(cssW = this.canvas.clientWidth || this.canvas.width, cssH = this.canvas.clientHeight || this.canvas.height): number {
    if (this.opts.pixelRatio) return this.opts.pixelRatio;
    const g: any = globalThis as any;
    const dpr = Math.min(this.opts.maxPixelRatio ?? 2, Math.max(1, g.devicePixelRatio || 1));
    const st: any = this.player.stage;
    const eff = effectivePixelRatio(dpr, cssW, cssH, st.stageWidth, st.stageHeight, this.scaleMode(), this.stageScaleCap());
    // Never below the CSS resolution (unless configured, or in the dynamic-resolution last-resort step): CSS
    // upscaling of the canvas blurs text and all art.
    if (this.belowCssFloor) return eff;
    return Math.max(eff, Math.min(dpr, this.opts.minPixelRatio ?? 1));
  }

  private scaleMode(): HostScaleMode {
    if (this.opts.scaleMode) return this.opts.scaleMode;
    const m = String((this.player.stage as any).scaleMode || 'showAll');
    return m === 'exactFit' || m === 'noBorder' || m === 'noScale' ? m : 'showAll';
  }

  /** Recompute backing-store size and viewport from the canvas' CSS size (called automatically). */
  resize(): void {
    this.initDynamicCap();
    const c = this.canvas;
    const cssW = c.clientWidth || c.width;
    const cssH = c.clientHeight || c.height;
    const st: any = this.player.stage;
    const dpr = this.pixelRatio();
    const mode = this.scaleMode();
    const wide = mode === 'showAll' ? this.wideLimits() : null;
    const v = computeViewport(cssW, cssH, dpr, st.stageWidth, st.stageHeight, mode, wide);
    // (a canvas resized is cleared: the next frame is drawn whatever art it lacks, never a held empty canvas)
    if (c.width !== v.width || c.height !== v.height) this.cleared = true;
    if (c.width !== v.width) c.width = v.width;
    if (c.height !== v.height) c.height = v.height;
    const stageRect = { x: 0, y: 0, width: st.stageWidth, height: st.stageHeight };
    const content = wide ? wideContent(st.stageWidth, st.stageHeight, wide) : stageRect;
    let vp = v.viewport;
    let kb: StageRect | null = null;
    if (mode === 'showAll') {
      // The field above an on-screen keyboard (fitted into the canvas' part above it), else the game's focus rectangle.
      kb = this.keyboardFocus(cssW, cssH);
      const availH = Math.max(1, cssH - this.keyboardInset);
      const focus = kb ?? this.focusRect();
      const fv = focus && computeFocusViewport(cssW, kb ? availH : cssH, dpr, st.stageWidth, st.stageHeight, focus, content);
      if (fv) vp = fv;
      else if (kb && this.keyboardInset > 0) vp = liftAbove(vp, kb, Math.round(availH * dpr));
    }
    this.viewport = vp;
    // (before the view's RESIZE: its listeners know whether it is the keyboard's)
    st.$keyboardView = !!kb;
    // What the game is told: the shown part of the stage and its margin, and the part of that clear of the safe insets.
    if (wide) {
      const shown = shownArea(vp, v.width, v.height, content) ?? stageRect;
      const i = this.safeInsets;
      const inner: Viewport = { ...vp, tx: vp.tx - i.left * dpr, ty: vp.ty - i.top * dpr };
      const safe = shownArea(inner, v.width - (i.left + i.right) * dpr, v.height - (i.top + i.bottom) * dpr, shown) ?? shown;
      this.visibleArea = shown;
      // CSS px per stage px (the backing store holds v.width device px over the canvas' cssW).
      st.$setView?.(shown, safe, v.width > 0 ? (vp.scale * cssW) / v.width : 1);
    } else {
      this.visibleArea = null;
      st.$setView?.(null, null);
    }
    this.dirty = true;
    this.fullNext = true;
  }

  private focusRect(): StageRect | null {
    try {
      return this.opts.focusRect ? this.opts.focusRect() : null;
    } catch {
      return null;
    }
  }

  private checkResize(): void {
    const c = this.canvas;
    const f = this.keyboardFocus(c.clientWidth || c.width, c.clientHeight || c.height) ?? this.focusRect();
    const w = this.wideLimits();
    const i = this.safeInsets;
    const key =
      `${c.clientWidth}x${c.clientHeight}@${this.pixelRatio()}:${this.scaleMode()}${f ? `|${f.x},${f.y},${f.width},${f.height}` : ''}` +
      `|kb${this.keyboardInset}|${w ? `w${w.width}x${w.height}` : ''}|${i.left},${i.top},${i.right},${i.bottom}`;
    if (key !== this.lastCss) {
      this.lastCss = key;
      this.resize();
    }
  }

  /** Client (CSS px) -> stage coordinates. */
  toStage(clientX: number, clientY: number): [number, number] {
    const r = this.canvas.getBoundingClientRect();
    return clientToStage(clientX, clientY, r, this.canvas.width, this.canvas.height, this.viewport);
  }

  /** Redraw the whole frame at the next opportunity (something outside the display list changed: art decoded...). */
  requestRender(): void {
    this.dirty = true;
    this.fullNext = true;
  }

  /** The next render is drawn whole (damage rendering). */
  private fullNext = true;

  /** Draw the stage now. */
  /** Current device-pixels-per-stage-pixel cap (lowered by dynamic resolution when rendering is slow). */
  private dynamicCap: number | null = null;
  /** Dynamic resolution's last-resort step below the CSS size is active (see adaptResolution). */
  private belowCssFloor = false;

  /** Whether dynamic resolution currently renders below the canvas CSS size (last-resort step on slow devices). */
  get belowCssSize(): boolean {
    return this.belowCssFloor;
  }
  private renderSamples: number[] = [];

  stageScaleCap(): number {
    const max = this.opts.maxStageScale ?? Infinity;
    return this.dynamicCap === null ? max : Math.min(max, Math.max(this.dynamicCap, this.textFloor()));
  }

  /** minStageScale now (0: none), never above the native scale. */
  private textFloor(): number {
    const m = this.opts.minStageScale;
    let v = 0;
    try {
      v = typeof m === 'function' ? m() : (m ?? 0);
    } catch {
      v = 0;
    }
    return v > 0 ? Math.min(v, this.nativeStageScale()) : 0;
  }

  private lastScaleChange = -Infinity;

  /**
   * Dynamic resolution: feed one render duration (ms); returns true when the cap changed (backing store resized).
   * Every 90 renders the median render time is checked. Above 14 ms the device-px-per-stage-px scale drops: first
   * straight to 2 (the resolution of the shape atlases) when above it, then in 0.25 steps down to the CSS
   * resolution (minPixelRatio). Only if frames at that floor still take (or are predicted to take) over 20 ms does it
   * go to 0.8x the CSS size (not below the 1x stage), returning to the floor once the predicted time fits 14 ms. It rises by 0.25 (up to
   * native) only if the time predicted for the larger scale (cost grows with scale²) stays under 9 ms. A 5 s
   * cooldown after each change avoids ping-pong.
   */
  adaptResolution(renderMs: number, nowMs: number = typeof performance !== 'undefined' ? performance.now() : Date.now()): boolean {
    if (this.opts.adaptiveResolution === false || this.opts.pixelRatio) return false;
    const s = this.renderSamples;
    s.push(renderMs);
    // PORT (mobile): with quickAdapt very slow frames are acted on after QUICK_ADAPT_SAMPLES renders instead of 90
    // (at 10 FPS a phone would otherwise wait 9 s per step, 5 s cooldown included, before its backing store shrinks).
    const quick = this.opts.quickAdapt === true && s.length >= QUICK_ADAPT_SAMPLES && s.length < 90;
    if (s.length < 90 && !quick) return false;
    const sorted = [...s].sort((a, b) => a - b);
    const median = sorted[s.length >> 1];
    if (quick && median <= QUICK_ADAPT_MS) return false;
    s.length = 0;
    const r: any = this.renderer;
    // Render times of frames that drew a large bitmap (the terrain in battle).
    const ss = this.sharpSamples;
    const sharpMedian = ss.length >= 60 ? [...ss].sort((a, b) => a - b)[ss.length >> 1] : null;
    if (ss.length >= 60) ss.length = 0;
    if (sharpMedian !== null && !r.sharpLargeBitmaps && nowMs >= this.sharpBlockedUntil && sharpMedian * 1.35 < 12) {
      // Terrain frames are cheap again (sharp bilinear costs ~+35% of such a frame on software rasterizers).
      r.sharpLargeBitmaps = true;
      this.requestRender();
      return true;
    }
    if (nowMs - this.lastScaleChange < 5000) return false;
    const cur = this.viewport.scale;
    if (!(cur > 0)) return false;
    const floor = this.floorStageScale();
    const max = Math.min(this.opts.maxStageScale ?? Infinity, this.nativeStageScale());
    // Quality ladder: when frames that draw the terrain are slow, drop its sharp bilinear first (restored above once
    // such frames are cheap again, at least 10 s later), then resolution. Slow screens that do not draw the terrain
    // (menus with heavy overdraw) never trigger this step.
    if (sharpMedian !== null && sharpMedian > 14 && r.sharpLargeBitmaps) {
      r.sharpLargeBitmaps = false;
      this.lastScaleChange = nowMs;
      this.sharpBlockedUntil = nowMs + 10000;
      this.requestRender();
      return true;
    }
    let next: number | null = null;
    const emergency = Math.max(1, floor * 0.8);
    const emergencyAllowed = this.opts.minPixelRatio === undefined;
    if (median > 14 && cur > floor + 1e-3) {
      next = Math.max(floor, cur > 2.01 ? 2 : cur - 0.25);
      // quickAdapt: straight to the scale predicted to fit 14 ms (cost grows with scale²), not one 0.25 step.
      if (this.opts.quickAdapt === true && median * (next / cur) ** 2 > 14) next = Math.max(floor, Math.min(next, cur * Math.sqrt(14 / median)));
      // Predicted to miss 50 FPS even at the CSS-size floor: straight to the last-resort scale below.
      if (next <= floor + 1e-3 && median * (next / cur) ** 2 > 20 && emergencyAllowed && emergency < next - 1e-3) next = emergency;
    }
    // Last resort on very slow (software-rasterized) devices: frames at the CSS-size floor still miss 50 FPS, so
    // render at 0.8x the CSS size (never below the 1x stage) and return to the floor as soon as it fits 14 ms again.
    else if (median > 20 && cur < floor + 1e-3 && emergency < cur - 1e-3 && emergencyAllowed) next = emergency;
    else if (cur < floor - 1e-3) {
      if (median * (floor / cur) ** 2 < 14) next = floor;
    }
    // After a slow period, stay down for 30 s: cheap screens (menus) must not bounce the scale back up before the
    // next heavy one (each change re-renders cached filter layers once).
    else if (this.dynamicCap !== null && cur < max - 1e-3 && nowMs >= this.upscaleBlockedUntil) {
      // Straight to native when the predicted cost allows it (fast GPU), otherwise one 0.25 step.
      if (median * (max / cur) ** 2 < 9) next = max;
      else {
        const up = Math.min(max, cur + 0.25);
        if (median * (up / cur) ** 2 < 9) next = up;
      }
    }
    if (next === null || Math.abs(next - cur) < 1e-3) return false;
    if (next < cur) this.upscaleBlockedUntil = nowMs + 30000;
    this.belowCssFloor = next < floor - 1e-3;
    this.dynamicCap = next >= max - 1e-3 ? null : next;
    this.lastScaleChange = nowMs;
    this.resize();
    this.lastCss = '';
    return true;
  }

  /**
   * Initial dynamic cap: when native resolution exceeds the shape-atlas resolution (2 device px per stage px, e.g.
   * 1920x1080 CSS at dpr 2) start there and let adaptResolution() raise it to native once render times prove cheap,
   * instead of starting at native and paying 3x the fill cost on slow (software-rasterized) devices.
   */
  private initDynamicCap(): void {
    if (this.dynamicInit || this.opts.adaptiveResolution === false || this.opts.pixelRatio) return;
    this.dynamicInit = true;
    if (this.opts.maxStageScale === undefined && this.nativeStageScale() > 2 + 1e-3) this.dynamicCap = Math.max(2, this.floorStageScale());
  }
  private dynamicInit = false;
  private upscaleBlockedUntil = -Infinity;
  private readonly sharpSamples: number[] = [];
  private sharpBlockedUntil = -Infinity;

  /** Device px per stage px at the native (capped) devicePixelRatio. */
  private nativeStageScale(): number {
    const g: any = globalThis as any;
    const dpr = Math.min(this.opts.maxPixelRatio ?? 2, Math.max(1, g.devicePixelRatio || 1));
    return dpr * this.cssStageScale();
  }

  /** Device px per stage px at the floor of dynamic resolution: the CSS resolution, or minStageScale (up to native). */
  private floorStageScale(): number {
    const g: any = globalThis as any;
    const dpr = Math.min(this.opts.maxPixelRatio ?? 2, Math.max(1, g.devicePixelRatio || 1));
    const css = Math.min(dpr, this.opts.minPixelRatio ?? 1) * this.cssStageScale();
    return Math.max(css, this.textFloor());
  }

  private cssStageScale(): number {
    const c = this.canvas;
    const st: any = this.player.stage;
    const sx = (c.clientWidth || c.width) / (st.stageWidth || 1);
    const sy = (c.clientHeight || c.height) / (st.stageHeight || 1);
    const mode = this.scaleMode();
    return mode === 'noScale' ? 1 : mode === 'showAll' ? Math.min(sx, sy) : Math.max(sx, sy);
  }

  /** Frames whose redraw was skipped because nothing visible changed (diagnostics). */
  skippedFrames = 0;
  private renderedVersion = -1;

  private hasFocusedInput(): boolean {
    const f: any = (this.player.stage as any).focus;
    return !!f && f.type === 'input';
  }

  /**
   * Draw the stage now, whole (a caller outside the frame loop: a test, a tool, a screenshot). The frame loop draws
   * through renderChanges(), which reaches this method too (a wrapper of `render` sees every frame).
   */
  render(): void {
    this.renderedVersion = rt().renderVersion;
    // A canvas just resized (cleared), a caret that blinks, art decoded: the frame is drawn whole.
    const full = !this.inFrame || this.fullNext || this.cleared || this.hasFocusedInput();
    this.cleared = false;
    this.fullNext = false;
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    const sharp0 = (this.renderer as any).largeBitmapDraws ?? 0;
    const d = this.renderer.damage;
    const full0 = d ? d.stats.full : 0;
    this.renderer.renderStage(this.player.stage as any, this.viewport, {
      letterbox: this.opts.letterbox !== false,
      letterboxColor: this.opts.letterboxColor ?? '#000',
      visible: this.visibleArea,
      full,
    });
    this.dirty = false;
    // Dynamic resolution learns from whole frames only: a partial frame's cost says nothing of the next whole one.
    if (d && d.stats.full === full0) {
      this.partialRenders++;
      return;
    }
    // ... and from the frames of the loop only: a frame a caller draws itself (a test comparing the canvas with what
    // the loop drew, a tool, a screenshot) must not resize the canvas or switch the terrain's sampling under it.
    if (t0 && this.inFrame) {
      const ms = performance.now() - t0;
      if (((this.renderer as any).largeBitmapDraws ?? 0) > sharp0) this.sharpSamples.push(ms);
      this.adaptResolution(ms);
      // A new resolution resized (and so cleared) the canvas after this frame was drawn: the frame is drawn again
      // now, whole, at the new size, so that a cleared canvas is never what the screen shows (a blink on a slow phone).
      if (this.cleared) {
        this.cleared = false;
        this.renderer.renderStage(this.player.stage as any, this.viewport, {
          letterbox: this.opts.letterbox !== false,
          letterboxColor: this.opts.letterboxColor ?? '#000',
          visible: this.visibleArea,
          full: true,
        });
      }
    }
  }

  /** Renders that redrew only what changed, or nothing (damage rendering; diagnostics, tests). */
  partialRenders = 0;

  /** render() runs for the frame loop (renderChanges). */
  private inFrame = false;

  /** Draw what changed since the last render (damage rendering; whole when it must be): a frame of the loop. */
  renderChanges(): void {
    this.inFrame = true;
    try {
      this.render();
    } finally {
      this.inFrame = false;
    }
  }

  /** The canvas was resized (and so cleared) since the last render: the next frame is shown (holdFrame). */
  private cleared = false;
  /** When the host began to keep its last frame over missing art (0: it does not; holdFrame). */
  private holdSince = 0;
  /** Frames not shown while a new screen's art loaded (holdFrame; diagnostics, tests). */
  heldFrames = 0;

  /** The last frame shown had all its art (holdFrame: only a whole frame is worth keeping on the canvas). */
  private lastComplete = false;

  /** Whether a frame was drawn yet (the page's own loading screen covers the canvas until then: src/main.ts). */
  get frameShown(): boolean {
    return this.renderedVersion >= 0;
  }

  /**
   * Whether this frame waits for its art (holdFrame): shape images of shown objects still loading while the canvas
   * shows a whole frame (or nothing yet: the first frame, under the page's loading screen), not for long. A frame
   * shown without its art (one after a wait too long) is followed by the next ones as they come, until one is whole
   * again.
   */
  private holding(): boolean {
    let ask = false;
    try {
      ask = !!this.opts.holdFrame && this.opts.holdFrame();
    } catch {
      ask = false;
    }
    const r: any = this.renderer;
    if (!ask || typeof r.missingImages !== 'function') {
      this.holdSince = 0;
      this.lastComplete = true;
      return false;
    }
    const missing = r.missingImages(this.player.stage);
    // (the first frame waits too: the main map's texts and icons on a black screen before its picture and plates)
    const first = this.renderedVersion < 0;
    if (missing === 0 || (!first && (!this.lastComplete || this.cleared))) {
      this.holdSince = 0;
      this.lastComplete = missing === 0;
      return false;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (!this.holdSince) this.holdSince = now;
    if (now - this.holdSince >= HOLD_FRAME_MS) {
      this.holdSince = 0;
      this.lastComplete = false;
      return false;
    }
    this.heldFrames++;
    return true;
  }

  private onFrame(ticks: number): void {
    this.checkResize();
    // Redraw only when something visible changed: every visual setter, timeline/display-list change, text
    // edit and BitmapData pixel change bumps runtime.renderVersion; an input field with focus needs the caret blink.
    // PORT: a frame whose art is still loading keeps the last one on the canvas for a moment (holdFrame).
    if (this.dirty || (ticks > 0 && (rt().renderVersion !== this.renderedVersion || this.hasFocusedInput()))) {
      if (this.holding()) this.dirty = true;
      else this.renderChanges();
    } else this.skippedFrames++;
    if (this.input) {
      // A finger's release the browser held up (a touch it cancelled on a busy page) after the frame that saw its move.
      this.input.afterFrame(ticks);
      this.input.syncTextFocus();
      this.input.updateCursor();
    }
  }

  /** Attach input, hook the player's frame loop and start it. */
  start(): void {
    if (this.started) return;
    this.started = true;
    if (this.input) this.input.attach();
    this.prevOnFrame = this.player.onFrame;
    const prev = this.prevOnFrame;
    this.player.onFrame = (p, n) => {
      if (prev) prev(p, n);
      this.onFrame(n);
    };
    const g: any = globalThis as any;
    if (this.opts.flushOnHide !== false && g.addEventListener) {
      const flush = () => SharedObject.flushAll();
      const vis = () => {
        if (g.document && g.document.visibilityState === 'hidden') flush();
      };
      g.addEventListener('pagehide', flush);
      g.addEventListener('beforeunload', flush);
      g.document?.addEventListener?.('visibilitychange', vis);
      this.cleanup.push(() => {
        g.removeEventListener('pagehide', flush);
        g.removeEventListener('beforeunload', flush);
        g.document?.removeEventListener?.('visibilitychange', vis);
      });
    }
    // Canvas 2D context loss (GPU reset, backgrounded tab on some platforms): every cached
    // texture/layer may be blank afterwards, so drop them and redraw from the runtime state.
    const restored = () => {
      this.renderer.surfaces.clear();
      this.renderer.clearFilterCache();
      this.requestRender();
    };
    const c: any = this.canvas;
    if (typeof c.addEventListener === 'function') {
      c.addEventListener('contextrestored', restored);
      this.cleanup.push(() => c.removeEventListener('contextrestored', restored));
    }
    this.checkResize();
    // (the first frame too waits for its art where the host holds frames: holdFrame)
    if (this.holding()) this.dirty = true;
    else this.render();
    this.player.start();
  }

  /** Stop the frame loop (input stays attached; call destroy() to detach everything). */
  stop(): void {
    this.player.stopLoop();
  }

  destroy(): void {
    this.stop();
    if (this.input) this.input.detach();
    for (const f of this.cleanup) f();
    this.cleanup.length = 0;
    if (this.started) this.player.onFrame = this.prevOnFrame;
    this.started = false;
  }
}
